'use client'

import { useAuth } from '@/hooks/use-auth'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { openOAuthPopup } from '@/lib/oauth/popup'
import { useTranslations } from "next-intl"


export type GoogleIntegrationMessage = {
  type: 'success' | 'error'
  message: string
}

export type OAuthStatus = 'connected' | 'token_expired' | 'error'

export type GoogleTokenInfo = {
  email: string | null
  provider: string
  connected_at: string | null
  updated_at: string | null
  use_for_sending?: boolean
  is_default?: boolean
  inbox_id?: string | null
  used_by_agents?: { id: string; name: string }[]
  oauth_status?: OAuthStatus
  last_error?: string | null
}

interface UseGoogleIntegrationOptions {
  autoRefresh?: boolean
  listenForOAuthCallback?: boolean
}

const DEFAULT_OPTIONS: Required<UseGoogleIntegrationOptions> = {
  autoRefresh: true,
  listenForOAuthCallback: true,
}

export function useGoogleIntegration(options?: UseGoogleIntegrationOptions) {
  const tUi = useTranslations("Feedback.UseIntegration")

  const { user, fetchWithAuth } = useAuth()
  const { autoRefresh, listenForOAuthCallback } = { ...DEFAULT_OPTIONS, ...options }

  const [tokens, setTokens] = useState<GoogleTokenInfo[]>([])
  const [message, setMessage] = useState<GoogleIntegrationMessage | null>(null)
  const [isLoading, setIsLoading] = useState(false)
  const [isConnecting, setIsConnecting] = useState(false)
  const [disconnectingEmail, setDisconnectingEmail] = useState<string | null>(null)
  const [updatingEmail, setUpdatingEmail] = useState<string | null>(null)

  const hasUser = Boolean(user)

  const loadTokens = useCallback(async () => {
    if (!user) {
      setTokens([])
      return
    }

    setIsLoading(true)
    try {
      const response = await fetchWithAuth('/api/auth/google/tokens', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
      })

      const data = await response.json().catch(() => ({}))
      const nextTokens = Array.isArray(data.tokens) ? (data.tokens as GoogleTokenInfo[]) : []
      setTokens(nextTokens)
    } catch (error) {
      console.error('Failed to load Google tokens', error)
      setTokens([])
    } finally {
      setIsLoading(false)
    }
  }, [fetchWithAuth, user])

  const connectGoogle = useCallback(async (opts?: { onConnected?: () => void }) => {
    if (!user) return

    setMessage(null)
    setIsConnecting(true)

    try {
      const response = await fetchWithAuth('/api/auth/google/connect', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ user_uuid: (user as { uuid?: string }).uuid }),
      })

      const data = await response.json().catch(() => ({}))
      if (!response.ok) {
        throw new Error(data?.error || 'Failed to initiate Google connection')
      }

      if (data?.auth_url) {
        openOAuthPopup({
          url: data.auth_url as string,
          name: 'google_oauth',
          onMessage: (result) => {
            if (result.google_oauth === 'connected') {
              setMessage({ type: 'success', message: tUi("googleAccountConnectedSuccessfully") })
              // Let callers (e.g. the voice Calendar Booking card, which has no
              // Inbox tab) refresh their own inbox/config list — the popup only
              // reloads this hook's own `tokens`, not consumers' state.
              opts?.onConnected?.()
            }
            void loadTokens()
          },
          onClose: () => void loadTokens(),
          errorKey: 'google_oauth_error',
          onError: (err) => setMessage({ type: 'error', message: err }),
        })
      }
    } catch (error: unknown) {
      setMessage({
        type: 'error',
        message: error instanceof Error ? error.message : 'Failed to connect Google',
      })
    } finally {
      setIsConnecting(false)
    }
  }, [fetchWithAuth, user, loadTokens])

  const disconnectGoogle = useCallback(
    async (email: string | null) => {
      if (!user) return

      setMessage(null)
      setDisconnectingEmail(email ?? '__NULL__')

      try {
        const response = await fetchWithAuth('/api/auth/google/disconnect', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ email }),
        })

        const data = await response.json().catch(() => ({}))
        if (!response.ok) {
          throw new Error(data?.error || 'Failed to disconnect Google account')
        }

        setTokens((prev) => prev.filter((token) => token.email !== email))
        setMessage({ type: 'success', message: tUi("disconnectedSuccessfully") })
      } catch (error: unknown) {
        setMessage({
          type: 'error',
          message: error instanceof Error ? error.message : 'Failed to disconnect Google account',
        })
      } finally {
        setDisconnectingEmail(null)
      }
    },
    [fetchWithAuth, user],
  )

  const updateUseForSending = useCallback(
    async (email: string | null, useForSending: boolean) => {
      if (!user) return

      setMessage(null)
      setUpdatingEmail(email ?? '__NULL__')

      try {
        const response = await fetchWithAuth('/api/auth/google/use-for-sending', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ email, use_for_sending: useForSending }),
        })

        const data = await response.json().catch(() => ({}))
        if (!response.ok) {
          throw new Error(data?.error || 'Failed to update email settings')
        }

        // Update only the specified token (multiple can be enabled)
        setTokens((prev) =>
          prev.map((token) => ({
            ...token,
            use_for_sending: token.email === email ? useForSending : token.use_for_sending,
          })),
        )
        setMessage({
          type: 'success',
          message: useForSending ? 'Email enabled for sending' : 'Email disabled for sending',
        })
      } catch (error: unknown) {
        setMessage({
          type: 'error',
          message: error instanceof Error ? error.message : 'Failed to update email settings',
        })
      } finally {
        setUpdatingEmail(null)
      }
    },
    [fetchWithAuth, user],
  )

  const updateIsDefault = useCallback(
    async (email: string | null, isDefault: boolean) => {
      if (!user) return

      setMessage(null)
      setUpdatingEmail(email ?? '__NULL__')

      try {
        const response = await fetchWithAuth('/api/auth/google/set-default', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ email, is_default: isDefault }),
        })

        const data = await response.json().catch(() => ({}))
        if (!response.ok) {
          throw new Error(data?.error || 'Failed to update default email')
        }

        // Update tokens locally - only one can be default
        setTokens((prev) =>
          prev.map((token) => ({
            ...token,
            is_default: token.email === email ? isDefault : false,
          })),
        )
        setMessage({
          type: 'success',
          message: isDefault ? 'Set as default email' : 'Removed as default email',
        })
      } catch (error: unknown) {
        setMessage({
          type: 'error',
          message: error instanceof Error ? error.message : 'Failed to update default email',
        })
      } finally {
        setUpdatingEmail(null)
      }
    },
    [fetchWithAuth, user],
  )

  useEffect(() => {
    if (!autoRefresh) return
    void loadTokens()
  }, [autoRefresh, loadTokens])

  useEffect(() => {
    if (!listenForOAuthCallback || typeof window === 'undefined') return

    const url = new URL(window.location.href)
    const success = url.searchParams.get('google_oauth')
    const error = url.searchParams.get('google_oauth_error')

    if (success === 'connected') {
      setMessage({ type: 'success', message: tUi("googleAccountConnectedSuccessfully") })
      void loadTokens()
      url.searchParams.delete('google_oauth')
      window.history.replaceState({}, document.title, url.pathname + url.search)
    } else if (error) {
      setMessage({ type: 'error', message: decodeURIComponent(error) })
      url.searchParams.delete('google_oauth_error')
      window.history.replaceState({}, document.title, url.pathname + url.search)
    }
  }, [listenForOAuthCallback, loadTokens])

  const latestActivity = useMemo(() => {
    if (tokens.length === 0) return null
    const sorted = [...tokens].sort((a, b) => {
      const aTime = new Date(a.connected_at ?? a.updated_at ?? 0).getTime()
      const bTime = new Date(b.connected_at ?? b.updated_at ?? 0).getTime()
      return bTime - aTime
    })
    return sorted[0]?.connected_at || sorted[0]?.updated_at || null
  }, [tokens])

  return {
    tokens,
    message,
    isLoading,
    isConnecting,
    disconnectingEmail,
    updatingEmail,
    hasUser,
    latestActivity,
    connectGoogle,
    disconnectGoogle,
    updateUseForSending,
    updateIsDefault,
    reloadTokens: loadTokens,
    clearMessage: () => setMessage(null),
  }
}
